import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";

import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow } from "@/components/marketing/Layout";
import { TOPICS_DE } from "@/lib/topics.de";
import { setLanguage } from "@/i18n";

const SITE = "https://realitytwin.io";

export const Route = createFileRoute("/de/themen/")({
  head: () => {
    const seo = buildSeo({
      path: "/de/themen",
      title: "Wie agentische KI im B2B wirklich funktioniert",
      description:
        "Sechs Erklärungen zu agentischer KI, KI-zu-KI-Geschäft, Vertrauen und Rechtssicherheit — jede davon ehrlich darüber, was heute funktioniert und was nicht.",
    });
    return {
      ...seo,
      links: [
        ...seo.links,
        { rel: "alternate", hrefLang: "de", href: `${SITE}/de/themen` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}/topics` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/topics` },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Wie agentische KI im B2B wirklich funktioniert",
            inLanguage: "de",
            url: `${SITE}/de/themen`,
            hasPart: TOPICS_DE.map((t) => ({
              "@type": "Article",
              headline: t.metaTitle,
              url: `${SITE}/de/themen/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: TopicsIndexDe,
});

function TopicsIndexDe() {
  useEffect(() => {
    void setLanguage("de");
  }, []);

  return (
    <MarketingLayout>
      <div className="py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow>Themen</Eyebrow>
          <h1 className="mt-4 font-serif text-[36px] sm:text-[56px] leading-[1.03] tracking-[-0.03em]">
            Wie agentische KI im B2B wirklich funktioniert
          </h1>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted-foreground">
            Sechs Erklärungen zu dem Wandel, der dieses Produkt sinnvoll macht. Jede nennt, was
            heute funktioniert und was nicht — denn in einem so überverkauften Feld ist die
            überprüfbare Seite mehr wert als die, die am meisten verspricht.
          </p>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-10 grid sm:grid-cols-2 gap-4">
          {TOPICS_DE.map((topic) => (
            <Link
              key={topic.slug}
              to="/de/themen/$slug"
              params={{ slug: topic.slug }}
              className="group rounded-2xl hairline bg-card p-5 hover:bg-cream transition flex flex-col"
            >
              <div className="font-serif text-[20px] tracking-[-0.02em]">{topic.name}</div>
              <p className="mt-2 flex-1 text-[14px] leading-[1.6] text-muted-foreground">
                {topic.heroLead}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium">
                Lesen
                <ArrowRight
                  className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition"
                  strokeWidth={2}
                />
              </span>
            </Link>
          ))}
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-10">
          <Link to="/topics" className="text-[13px] text-muted-foreground hover:text-foreground">
            Read these pages in English →
          </Link>
        </div>
      </div>
    </MarketingLayout>
  );
}
